/**
 * EditModal Component
 *
 * Modal for editing posts with two modes:
 * 1. AI Rewrite - Send custom instructions to AI for content regeneration
 * 2. Manual Edit - Direct editing of all content fields
 */

import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

function EditModal({ open, post, onClose, onAIRewrite, onManualEdit, loading, loadingStage }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [activeTab, setActiveTab] = useState(0);
  const [aiInstructions, setAiInstructions] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");

  // Manual edit fields - content and social copies
  const [editedContent, setEditedContent] = useState("");
  const [editedLinkedinCopy, setEditedLinkedinCopy] = useState("");
  const [editedXCopy, setEditedXCopy] = useState("");
  const [editedThreadsCopy, setEditedThreadsCopy] = useState("");

  // Update fields when post changes
  useEffect(() => {
    if (post) {
      setEditedContent(post.content || "");
      setEditedLinkedinCopy(post.linkedinCopy || "");
      setEditedXCopy(post.xCopy || "");
      setEditedThreadsCopy(post.threadsCopy || "");
    }
  }, [post]);

  if (!post) return null;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAIRewriteSubmit = () => {
    if (!aiInstructions.trim() && !customPrompt.trim()) {
      alert("Please provide edit instructions or a custom prompt");
      return;
    }

    onAIRewrite(post.id, {
      editInstructions: aiInstructions,
      customPrompt: customPrompt,
    });
  };

  const handleManualEditSubmit = () => {
    if (activeTab === 1 && !editedContent.trim()) {
      alert("Content cannot be empty");
      return;
    }

    const updatedData =
      activeTab === 1
        ? { content: editedContent }
        : {
            linkedinCopy: editedLinkedinCopy,
            xCopy: editedXCopy,
            threadsCopy: editedThreadsCopy,
          };

    onManualEdit(post.id, updatedData);
  };

  const getLoadingMessage = () => {
    switch (loadingStage) {
      case "starting":
        return "Starting edit workflow...";
      case "updating-status":
        return "Updating post status...";
      case "triggering-workflow":
        return "Triggering AI rewrite...";
      case "processing":
        return "AI is rewriting content...";
      case "complete":
        return "Complete!";
      default:
        return "Processing...";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      {/* Header */}
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        borderBottom="1px solid"
        borderColor="grey.300"
      >
        <MDTypography variant="h4" fontWeight="bold">
          Edit Post
        </MDTypography>
        <IconButton onClick={onClose} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </MDBox>

      {/* Tabs */}
      <MDBox borderBottom="1px solid" borderColor="grey.300">
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="AI Rewrite" />
          <Tab label="Edit Blog Content" />
          <Tab label="Edit Social Copies" />
        </Tabs>
      </MDBox>

      <DialogContent
        sx={{
          p: 3,
          minHeight: "400px",
        }}
      >
        {loading ? (
          <MDBox display="flex" flexDirection="column" alignItems="center" py={5}>
            <CircularProgress size={60} color="info" />
            <MDTypography variant="h6" color="text" mt={3}>
              {getLoadingMessage()}
            </MDTypography>
          </MDBox>
        ) : (
          <>
            {/* AI Rewrite Tab */}
            {activeTab === 0 && (
              <MDBox>
                <MDTypography variant="body2" color="text" mb={3}>
                  Provide instructions for the AI to rewrite your content. The AI will maintain the
                  core message while incorporating your feedback.
                </MDTypography>

                {/* Edit Instructions */}
                <MDBox mb={3}>
                  <MDTypography variant="body2" fontWeight="bold" mb={1}>
                    Edit Instructions *
                  </MDTypography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="e.g., Make the tone more casual and add practical examples"
                    value={aiInstructions}
                    onChange={(e) => setAiInstructions(e.target.value)}
                    variant="outlined"
                  />
                </MDBox>

                {/* Custom Prompt */}
                <MDBox mb={3}>
                  <MDTypography variant="body2" fontWeight="bold" mb={1}>
                    Additional Instructions (Optional)
                  </MDTypography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="e.g., Focus on the benefits for small businesses"
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    variant="outlined"
                  />
                </MDBox>

                {/* Current Content Preview */}
                <MDBox
                  mb={2}
                  p={2}
                  sx={{
                    backgroundColor: "grey.100",
                    borderRadius: 2,
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  <MDTypography variant="caption" fontWeight="bold" display="block" mb={1}>
                    Current Article Preview:
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    {post.article?.substring(0, 500)}
                    {post.article?.length > 500 ? "..." : ""}
                  </MDTypography>
                </MDBox>

                <MDTypography variant="caption" color="text" fontStyle="italic">
                  Note: AI rewrite will regenerate the article and all social media posts based on
                  your instructions.
                </MDTypography>
              </MDBox>
            )}

            {/* Manual Edit Tab */}
            {activeTab === 1 && (
              <MDBox>
                <MDTypography variant="body2" color="text" mb={3}>
                  Edit the post content. Changes will be saved to Supabase when you click Save.
                </MDTypography>

                {/* Content */}
                <MDBox mb={3}>
                  <MDTypography variant="body2" fontWeight="bold" mb={1}>
                    Content *
                  </MDTypography>
                  <TextField
                    fullWidth
                    multiline
                    rows={16}
                    placeholder="Edit your post content here..."
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    variant="outlined"
                  />
                  <MDTypography variant="caption" color="text" mt={0.5}>
                    {editedContent.length} characters
                  </MDTypography>
                </MDBox>
              </MDBox>
            )}

            {/* Edit Social Copies Tab */}
            {activeTab === 2 && (
              <MDBox>
                <MDTypography variant="body2" color="text" mb={3}>
                  Edit the social media copies for each platform individually. Character limits are
                  shown for reference.
                </MDTypography>

                {/* LinkedIn Copy */}
                <MDBox mb={3}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <MDTypography variant="body2" fontWeight="bold">
                      <Icon
                        sx={{
                          fontSize: "1rem",
                          mr: 0.5,
                          verticalAlign: "middle",
                          color: "#0A66C2",
                        }}
                      >
                        work
                      </Icon>
                      LinkedIn Copy
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color={editedLinkedinCopy.length > 3000 ? "error" : "text"}
                    >
                      {editedLinkedinCopy.length} / 3000
                    </MDTypography>
                  </MDBox>
                  <TextField
                    multiline
                    rows={6}
                    fullWidth
                    value={editedLinkedinCopy}
                    onChange={(e) => setEditedLinkedinCopy(e.target.value)}
                    placeholder="Enter LinkedIn copy..."
                    variant="outlined"
                  />
                </MDBox>

                {/* X Copy */}
                <MDBox mb={3}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <MDTypography variant="body2" fontWeight="bold">
                      <Icon
                        sx={{
                          fontSize: "1rem",
                          mr: 0.5,
                          verticalAlign: "middle",
                          color: "#000000",
                        }}
                      >
                        tag
                      </Icon>
                      X Copy
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color={editedXCopy.length > 280 ? "error" : "text"}
                    >
                      {editedXCopy.length} / 280
                    </MDTypography>
                  </MDBox>
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    value={editedXCopy}
                    onChange={(e) => setEditedXCopy(e.target.value)}
                    placeholder="Enter X (Twitter) copy..."
                    variant="outlined"
                  />
                </MDBox>

                {/* Threads Copy */}
                <MDBox mb={3}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <MDTypography variant="body2" fontWeight="bold">
                      <Icon
                        sx={{
                          fontSize: "1rem",
                          mr: 0.5,
                          verticalAlign: "middle",
                          color: "#A855F7",
                        }}
                      >
                        forum
                      </Icon>
                      Threads Copy
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color={editedThreadsCopy.length > 500 ? "error" : "text"}
                    >
                      {editedThreadsCopy.length} / 500
                    </MDTypography>
                  </MDBox>
                  <TextField
                    multiline
                    rows={5}
                    fullWidth
                    value={editedThreadsCopy}
                    onChange={(e) => setEditedThreadsCopy(e.target.value)}
                    placeholder="Enter Threads copy..."
                    variant="outlined"
                  />
                </MDBox>
              </MDBox>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid", borderColor: "grey.300" }}>
        <MDButton onClick={onClose} variant="outlined" color="dark" disabled={loading}>
          Cancel
        </MDButton>

        {activeTab === 0 ? (
          <MDButton
            onClick={handleAIRewriteSubmit}
            variant="gradient"
            color="info"
            disabled={loading || (!aiInstructions.trim() && !customPrompt.trim())}
          >
            {loading ? "Processing..." : "Rewrite with AI"}
          </MDButton>
        ) : (
          <MDButton
            onClick={handleManualEditSubmit}
            variant="gradient"
            color="success"
            disabled={loading || (activeTab === 1 && !editedContent.trim())}
          >
            {loading ? "Saving..." : "Save Changes"}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

EditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  post: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onAIRewrite: PropTypes.func.isRequired,
  onManualEdit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  loadingStage: PropTypes.string,
};

EditModal.defaultProps = {
  loading: false,
  loadingStage: "idle",
};

export default EditModal;
